import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import './savedCardPay.css';
import CardBrandMark from './CardBrandMark';
import { cardLast4 } from '../ClientWallet/savedCardUtils';

/**
 * SavedCardPay — pay with a stored card: brand, name, last four, ONE CVV box
 * on the same row, and Pay (2026-08-13). No card re-entry, no redirect.
 *
 * The CVV is typed here and travels in a single request to our server, which
 * forwards it to the gateway. It is never stored, never logged, and never
 * sent on an automatic (merchant-initiated) charge.
 *
 * 3DS: when the bank wants to authenticate, the challenge renders in an
 * iframe INSIDE our overlay (the relay lives at /payments/3ds/*). The result
 * is always read back from the server — the browser never decides an outcome.
 *
 *   card        the saved card row from GET /api/cards
 *   payLabel    e.g. "Pay AED 2,500.00"
 *   charge(cvv) → the surface's own endpoint; resolves to the server payload
 *   onPaid / onFailed  verified outcomes
 */
export default function SavedCardPay({ card, payLabel, disabled = false, compact = false, cvv: cvvProp, onCvvChange, onBusyChange, charge, onPaid, onFailed, actionSlot = null }) {
    const [ownCvv, setOwnCvv] = useState('');
    const cvv = compact ? (cvvProp || '') : ownCvv;
    const setCvv = (v) => (compact ? onCvvChange?.(v) : setOwnCvv(v));
    const [busy, setBusy] = useState(false);
    const [stage, setStage] = useState('idle'); // idle | paying | challenge | confirming
    const [error, setError] = useState('');
    const [locked, setLocked] = useState(false);
    const challengeFormRef = useRef(null);
    const orderRefRef = useRef(null);

    const cvvValid = /^\d{3,4}$/.test(cvv);

    // The CVV field and the card picker live in the PARENT (the card is shown
    // once, with the code in its row). They must freeze while a payment is in
    // flight — a client could otherwise retype the code over a live payment
    // (2026-08-13). Locked stays frozen: that outcome is unknown.
    useEffect(() => {
        onBusyChange?.(busy || locked);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busy, locked]);

    // Poll the server for the settled outcome — the ONLY source of truth.
    const settle = async (orderRef) => {
        setStage('confirming');
        const deadline = Date.now() + 90000;
        let delay = 1500;
        let result = null;
        while (Date.now() < deadline) {
            const v = await axios.post('/api/payments/verify', { order_ref: orderRef });
            result = v.data;
            if (result?.outcome && result.outcome !== 'pending') break;
            // Captured per iteration: the timer callback must not close over a
            // `delay` the next pass reassigns (no-loop-func).
            const wait = delay;
            await new Promise((r) => setTimeout(r, wait));
            delay = Math.min(delay * 1.4, 6000);
        }
        if (result?.outcome === 'paid' || result?.outcome === 'verified') {
            // The ORDER REF travels with the outcome (2026-08-14). /verify's
            // payload does not carry it, and it is the only handle that finds
            // the receipt when the payment id lives on the SETTLEMENT rather
            // than the deposit (a top-up that also paid invoices).
            onPaid?.({ ...result, order_ref: orderRef });
            return;
        }
        if (result?.outcome === 'pending') {
            setLocked(true);
            setError('Your bank is still confirming this payment — we’ll update your wallet automatically. Please don’t pay again.');
        } else {
            setError('The payment did not go through — you have not been charged. Please try again.');
            setCvv('');
        }
        onFailed?.(result);
    };

    // The bank's page posts its result to our notification URL, which replies
    // with a page that messages us. That message is our cue to settle.
    useEffect(() => {
        const onMessage = (e) => {
            if (e.origin !== window.location.origin) return;
            if (e.data?.source !== 'hhc-3ds') return;
            if (orderRefRef.current && e.data.orderRef !== orderRefRef.current) return;
            setStage('confirming');
            settle(orderRefRef.current).finally(() => setBusy(false));
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const runChallenge = async (orderRef) => {
        const bi = {
            userAgent: navigator.userAgent,
            language: navigator.language,
            colorDepth: window.screen?.colorDepth,
            screenHeight: window.screen?.height,
            screenWidth: window.screen?.width,
            timeZoneOffset: new Date().getTimezoneOffset(),
            javaEnabled: typeof navigator.javaEnabled === 'function' ? navigator.javaEnabled() : false,
        };
        const authed = await axios.post('/api/payments/3ds/authenticate', {
            order_ref: orderRef,
            browser_info: bi,
        });
        if (authed.data?.outcome === 'settled') {
            await settle(orderRef);
            return;
        }
        // Post the challenge to the bank, targeting the iframe in our overlay.
        // Two shapes exist in the wild: EMV 3DS2 (creq) and legacy 3DS 1.x
        // (PaReq + MD + TermUrl) — sandbox outlets still answer with the
        // latter, so both are driven from the same hidden form.
        setStage('challenge');
        postChallenge(authed.data.acs_url, authed.data.outcome === 'challenge_legacy'
            ? { PaReq: authed.data.pareq, MD: authed.data.md, TermUrl: authed.data.term_url }
            : { creq: authed.data.creq });
    };

    /** Post whichever challenge shape into the overlay's iframe. */
    const postChallenge = (acsUrl, fields) => {
        setStage('challenge');
        const form = challengeFormRef.current;
        const set = (name, value) => {
            let input = form.querySelector(`input[name="${name}"]`);
            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                form.appendChild(input);
            }
            input.value = value == null ? '' : value;
        };
        form.action = acsUrl;
        Object.entries(fields).forEach(([k, v]) => set(k, v));
        form.submit();
        // From here the flow continues in the message listener above.
    };

    const pay = async () => {
        if (busy || locked || disabled || !cvvValid) return;
        setError('');
        setBusy(true);
        setStage('paying');
        try {
            const started = await charge(cvv);
            setCvv(''); // the code has done its job — drop it immediately
            if (!started) { setBusy(false); setStage('idle'); return; }
            orderRefRef.current = started.order_ref;

            if (started.outcome === 'three_ds') {
                // Legacy challenges arrive complete with the charge response;
                // EMV ones need the extra authenticate round-trip.
                if (started.challenge === 'legacy' && started.acs_url) {
                    postChallenge(started.acs_url, {
                        PaReq: started.pareq, MD: started.md, TermUrl: started.term_url,
                    });
                    return;
                }
                await runChallenge(started.order_ref);
                return; // settled by the listener
            }
            if (started.outcome === 'three_ds_redirect' && started.three_ds_url) {
                window.location.assign(started.three_ds_url);
                return;
            }
            if (started.outcome === 'paid' || started.outcome === 'verified') {
                onPaid?.(started);
                return;
            }
            await settle(started.order_ref);
        } catch (err) {
            setError(err?.response?.data?.error || 'Could not process the payment. Please try again.');
            setCvv('');
            onFailed?.(null);
        } finally {
            if (stage !== 'challenge') setBusy(false);
        }
    };

    // Defined here, rendered either in place or portalled into the host's
    // action row (see actionSlot) — one definition, so the two placements can
    // never drift in behaviour.
    const payButton = (
        <button
            type="button"
            className={`scp__payBtn ${actionSlot ? 'scp__payBtn--slotted' : ''}`}
            onClick={pay}
            disabled={disabled || busy || locked || !cvvValid}
        >
            {busy ? 'Please wait…' : payLabel}
        </button>
    );

    return (
        <div className="scp">
            {/* compact: the picker directly above already names the card —
                repeating it here just showed the same Visa twice (the owner
                2026-08-13). Only the security code belongs in this row. */}
            {!compact && (
            <div className="scp__row">
                {compact ? (
                    <label className="scp__label" htmlFor="scp-cvv">Security code</label>
                ) : (
                    <>
                        <CardBrandMark scheme={card?.scheme} />
                        <div className="scp__name">
                            {String(card?.nickname || '').trim() || 'Saved card'} •••• {cardLast4(card?.masked_pan)}
                        </div>
                    </>
                )}
                <input
                    id="scp-cvv"
                    className="scp__cvv"
                    type="password"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    maxLength={4}
                    placeholder="CVV"
                    aria-label="Card security code"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter') pay(); }}
                    disabled={busy || locked}
                />
            </div>
            )}

            <div className="scp__hint">
                For your security your bank asks for the 3 or 4 digit code on your card.
                We never store it.
            </div>

            {error && <div className="scp__error">{error}</div>}

            {/* ONE status surface while a payment is in flight (the owner
                2026-08-13: the button/modal states read as a muddle). The
                button stops narrating; this block says where we are, in
                plain words, with a spinner that matches the brand. */}
            {busy && !error && (
                <div className="scp__status" role="status" aria-live="polite">
                    <span className="scp__statusSpinner" aria-hidden="true" />
                    <span className="scp__statusText">
                        <strong>
                            {stage === 'challenge' ? 'Waiting for your bank'
                                : stage === 'confirming' ? 'Confirming your payment'
                                    : 'Contacting your bank'}
                        </strong>
                        <span className="scp__statusSub">
                            {stage === 'challenge'
                                ? 'Approve the request from your bank to finish. This window stays open.'
                                : stage === 'confirming'
                                    ? 'Almost done — we’re checking the result with your bank.'
                                    : 'This usually takes a few seconds. Please don’t close this window.'}
                        </span>
                    </span>
                </div>
            )}

            {/* THE PAY BUTTON MAY LIVE IN THE HOST'S ACTION ROW (the owner
                2026-08-14: "you still haven't put the pay button next to the
                back button… which makes the form a bit weird"). A surface with
                a Back/Continue row passes a slot node and the button is
                PORTALLED into it, so every step ends in one consistent
                [Back][Pay] row instead of a lone Back with the Pay floating
                above it. The button keeps all of its state here — busy, locked,
                CVV validity — because only this component knows it. No slot:
                it renders in place, exactly as before. */}
            {actionSlot
                ? createPortal(payButton, actionSlot)
                : payButton}

            {/* The bank's challenge, inside our page. */}
            <div className={`scp__overlay ${stage === 'challenge' ? 'is-active' : ''}`}>
                <div className="scp__overlayCard">
                    <div className="scp__overlayHead">
                        <div className="scp__overlayTitle">Confirm with your bank</div>
                        {/* Always an exit (2026-08-13: trapped on mobile).
                            Closing does NOT abandon the payment — we go
                            straight to reading the outcome from the server. */}
                        <button
                            type="button"
                            className="scp__overlayClose"
                            aria-label="Close"
                            onClick={async () => {
                                setStage('confirming');
                                // Tell the server the challenge was abandoned: if no
                                // money moved it settles as failed IMMEDIATELY rather
                                // than leaving the client "confirming" for an hour.
                                try {
                                    const r = await axios.post('/api/payments/3ds/abandon', { order_ref: orderRefRef.current });
                                    if (r.data?.outcome && r.data.outcome !== 'pending') {
                                        if (r.data.outcome === 'paid' || r.data.outcome === 'verified') {
                                            // Always attach the order ref: this
                                            // path used to hand the surface a
                                            // bare {outcome}, leaving its
                                            // confirmation with no handle to
                                            // find the receipt with.
                                            onPaid?.({ ...r.data, order_ref: orderRefRef.current });
                                        } else {
                                            setError('Card authentication was not completed — you have not been charged. Enter the security code again to retry.');
                                            setCvv('');
                                            onFailed?.(r.data);
                                        }
                                        setBusy(false);
                                        return;
                                    }
                                } catch { /* fall through to polling */ }
                                settle(orderRefRef.current).finally(() => setBusy(false));
                            }}
                        >
                            ×
                        </button>
                    </div>
                    <iframe title="Card authentication" name="hhc-3ds-frame" className="scp__frame" />
                </div>
            </div>
            <form ref={challengeFormRef} method="POST" target="hhc-3ds-frame" style={{ display: 'none' }}>
                <input type="hidden" name="creq" />
            </form>
        </div>
    );
}
